import StaticPage, { Section } from "../components/StaticPage";

export default function Privacy() {
    return (
        <StaticPage
            title="Privacy Policy · ZenZest"
            description="How weeddispensarynearme.com (operated for ZenZest) collects, uses, and protects your information. Last updated February 2026."
            canonical="https://weeddispensarynearme.com/privacy"
            eyebrow="Legal · last updated Feb 2026"
            h1="Privacy policy."
            intro="What we collect, how we use it, and the controls you have. Written in plain English."
            toc={[
                { id: "scope", label: "Scope" },
                { id: "what-we-collect", label: "What we collect" },
                { id: "how-we-use", label: "How we use it" },
                { id: "sharing", label: "Who we share with" },
                { id: "your-rights", label: "Your rights" },
                { id: "retention", label: "Retention" },
                { id: "minors", label: "Age (21+)" },
                { id: "contact", label: "Contact us" },
            ]}
        >
            <Section id="scope" title="Scope">
                <p>
                    This policy covers weeddispensarynearme.com and the connected
                    Dutchie checkout and Alpine IQ loyalty experiences operated for
                    ZenZest, a New York licensed adult-use cannabis retailer.
                </p>
            </Section>

            <Section id="what-we-collect" title="What we collect">
                <p>We collect three categories of information:</p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        <strong>You give us.</strong> Name, mobile phone, email,
                        delivery address, age-verification photo of your government
                        ID, payment metadata, and any preferences you share when
                        signing up for ZenCircle or texting our team.
                    </li>
                    <li>
                        <strong>We observe.</strong> Pages you view, products you
                        search, items you add to cart, IP address, browser type,
                        device, time on site, and approximate location (derived
                        from IP — used only for geofenced delivery eligibility).
                    </li>
                    <li>
                        <strong>Partners give us.</strong> Order status from
                        Dutchie (our point-of-sale), points and tier from Alpine
                        IQ (loyalty), aggregate ad-performance data from Google.
                    </li>
                </ul>
            </Section>

            <Section id="how-we-use" title="How we use it">
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>To process orders, verify age, and deliver product.</li>
                    <li>
                        To run ZenCircle rewards — apply your point multiplier,
                        send your sign-in PIN, and redeem points at checkout.
                    </li>
                    <li>
                        To send transactional and promotional SMS / email — only
                        after you opt in. Reply STOP to opt out at any time.
                    </li>
                    <li>
                        To improve the site — usage analytics, A/B tests, and
                        bug investigations. We do not sell raw analytics data.
                    </li>
                    <li>
                        To comply with NY Office of Cannabis Management
                        reporting obligations.
                    </li>
                </ul>
            </Section>

            <Section id="sharing" title="Who we share with">
                <p>
                    We share only what we need to, with a small set of operators
                    that we vet and contract with:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>
                        <strong>Dutchie</strong> — point of sale, online cart,
                        checkout, age verification.
                    </li>
                    <li>
                        <strong>Alpine IQ</strong> — loyalty, SMS dispatch,
                        contact &amp; newsletter management.
                    </li>
                    <li>
                        <strong>Payment processors / delivery providers</strong>{" "}
                        — only the minimum required to complete your order.
                    </li>
                    <li>
                        <strong>Government regulators</strong> — when lawfully
                        required (NY OCM, NYS Department of Taxation and Finance).
                    </li>
                </ul>
                <p>
                    We do <strong>not</strong> sell or rent personal information
                    to advertisers.
                </p>
            </Section>

            <Section id="your-rights" title="Your rights">
                <p>
                    Regardless of where you live, you can email{" "}
                    <a
                        href="mailto:privacy@zenzest.com"
                        className="text-zen-ink underline"
                    >
                        privacy@zenzest.com
                    </a>{" "}
                    to:
                </p>
                <ul className="list-disc pl-5 space-y-1.5">
                    <li>Request a copy of the data we hold on you.</li>
                    <li>Correct any inaccuracies.</li>
                    <li>Delete your account and associated data.</li>
                    <li>
                        Opt out of marketing SMS (reply STOP) or email (click
                        unsubscribe).
                    </li>
                </ul>
                <p>
                    Order records that we&rsquo;re required by NY law to keep
                    cannot be deleted on demand, but we&rsquo;ll explain what
                    must be retained and why.
                </p>
            </Section>

            <Section id="retention" title="How long we keep data">
                <p>
                    Order records: 5 years (NY regulatory requirement). Loyalty
                    profile: as long as your account is active, plus 18 months.
                    Marketing opt-ins: until you opt out. Analytics: aggregated and
                    anonymized after 14 months.
                </p>
            </Section>

            <Section id="minors" title="Age (21+ only)">
                <p>
                    This site and any associated services are for adults 21+
                    only. We do not knowingly collect data from minors. If you
                    believe a minor has provided information, contact us and
                    we&rsquo;ll delete it immediately.
                </p>
            </Section>

            <Section id="contact" title="Contact us">
                <p>
                    Questions, requests, complaints — email{" "}
                    <a
                        href="mailto:privacy@zenzest.com"
                        className="text-zen-ink underline"
                    >
                        privacy@zenzest.com
                    </a>{" "}
                    or write to: ZenZest Privacy, 272-06 Union Tpke, Queens, NY 11040.
                </p>
            </Section>
        </StaticPage>
    );
}
